import type {
  FetchMeResponse,
  FetchOnboardingData,
  FetchTransactionsResponse,
  GetPromoCodeInformationPayload,
  GetPromoCodeInformationResponse,
  PurchaseProductPayload,
  PurchaseProductResponse,
  RequestLoginPayload,
  RequestLoginResponse,
  RequestRegisterPayload,
  SaveProfileInfoPayload,
  SendCancelReasonPayload,
  SubmitOnboardingDataPayload,
  UpdatePaymentMethodPayload,
  ValidatePromoCodePayload,
  ValidatePromoCodeResponse,
} from "./auth.types";
import { ProductTypes } from "~/types";

export async function requestLogin(
  payload: RequestLoginPayload,
): Promise<RequestLoginResponse> {
  const { $api } = useNuxtApp();
  const credentials = `${payload.email}:${payload.password}`;
  const encodedCredentials = btoa(credentials);

  let path = "/sign-in";

  if (payload?.ott_code) {
    path = "/ott/sign-in";
  }

  return await $api("v4")(path, {
    method: "POST",
    body: { ott_code: payload?.ott_code },
    headers: {
      Authorization: `Basic ${encodedCredentials}`,
    },
  });
}

export async function requestRegister(
  payload: RequestRegisterPayload,
): Promise<RequestLoginResponse> {
  const { $api } = useNuxtApp();

  let path = "/register";

  if (payload.data.attributes?.ott_code) {
    path = "/ott/register";
  }

  return await $api("v4")(path, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function requestResetPassword(payload: { email: string }) {
  const { $api } = useNuxtApp();
  return await $api("v4")(
    `/subscribers/reset-password?email=${payload.email}`,
    {
      method: "POST",
    },
  );
}

export async function resetPassword(payload: {
  old_password: string;
  new_password: string;
  new_password_confirm: string;
}) {
  const { $api } = useNuxtApp();
  return await $api("v4")(`/subscribers/change-password?type=login%20page`, {
    method: "POST",
    body: payload,
  });
}

export async function changePassword(payload: {
  new_password: string;
  new_password_confirm: string;
}) {
  const { $api } = useNuxtApp();
  return await $api("v1")(`/subscribers/me/change-password`, {
    method: "POST",
    body: payload,
  });
}

export async function fetchMe(): Promise<FetchMeResponse> {
  const { $api } = useNuxtApp();
  return await $api("v4")(`/subscribers/me`);
}

export async function saveProfileInfo(payload: SaveProfileInfoPayload) {
  const { $api } = useNuxtApp();
  return await $api("v4")(`/subscribers/me/change-name`, {
    method: "POST",
    body: payload,
  });
}
export async function requestEmailCode(): Promise<FetchTransactionsResponse> {
  const { $api } = useNuxtApp();
  return await $api("v4")(`/subscribers/me/requestemailcode`);
}

export async function validateEmailCode(payload: {
  code: string;
}): Promise<FetchTransactionsResponse> {
  const { $api } = useNuxtApp();
  return await $api("v4")(`/subscribers/me/validateemailcode`, {
    method: "POST",
    body: payload,
  });
}

export async function fetchTransaction(): Promise<FetchTransactionsResponse> {
  const { $api } = useNuxtApp();
  return await $api("v4")(`/subscribers/me/transactions`);
}

export async function purchaseProduct(
  payload: PurchaseProductPayload,
): Promise<PurchaseProductResponse> {
  const { $api } = useNuxtApp();
  return await $api("v4")(`/subscribers/me/purchase`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function acceptUpgradeDiscount(payload: {
  interval: ProductTypes;
}): Promise<{ data: string }> {
  const { $api } = useNuxtApp();
  return await $api("v4")(`/subscribers/me/purchase/discount`, {
    method: "POST",
    body: payload,
  });
}

export async function sendCancelReason(
  payload: SendCancelReasonPayload,
): Promise<{ data: string }> {
  const { $api } = useNuxtApp();
  return await $api("v4")(`/subscribers/me/purchase/cancelreason`, {
    method: "POST",
    body: payload,
  });
}

export async function cancelSubscription(): Promise<{ data: string }> {
  const { $api } = useNuxtApp();
  return await $api("v4")(`/subscribers/me/purchase/cancel`, {
    method: "POST",
  });
}

export async function upgradeSubscription(payload: {
  product_type: ProductTypes;
  coupon?: string;
}): Promise<{ data: string }> {
  const { $api } = useNuxtApp();
  return await $api("v4")(`/subscribers/me/upgrade-subscription`, {
    method: "POST",
    body: payload,
  });
}

export async function validatePromoCode(
  payload: ValidatePromoCodePayload,
): Promise<ValidatePromoCodeResponse> {
  const { $api } = useNuxtApp();
  return await $api("v4")(`/subscribers/me/promovalidate`, {
    method: "POST",
    body: payload,
  });
}

export async function getPromoCodeInformation(
  payload: GetPromoCodeInformationPayload,
): Promise<GetPromoCodeInformationResponse> {
  const { $api } = useNuxtApp();
  return await $api("v4")(`/promocodeinformation?code=${payload.code}`);
}

export async function updatePaymentMethod(payload: UpdatePaymentMethodPayload) {
  const { $api } = useNuxtApp();
  return await $api("v4")(`/subscribers/me/updatepaymentmethod`, {
    method: "POST",
    body: payload,
  });
}

export async function requestSmsCode(payload: {
  phone: string;
}): Promise<FetchTransactionsResponse> {
  const { $api } = useNuxtApp();
  return await $api("v4")(`/subscribers/me/sms/send-code`, {
    method: "POST",
    body: payload,
  });
}

export async function validateSmsCode(payload: {
  phone: string;
  code: string;
}): Promise<FetchTransactionsResponse> {
  const { $api } = useNuxtApp();
  return await $api("v4")(`/subscribers/me/sms/confirm-code`, {
    method: "POST",
    body: payload,
  });
}

export async function fetchOnboardingData(): Promise<FetchOnboardingData> {
  const { $api } = useNuxtApp();
  return await $api("v4")(`/subscribers/me/onboarding`);
}

export async function submitOnboardingData(
  payload: SubmitOnboardingDataPayload,
) {
  const { $api } = useNuxtApp();
  return await $api("v4")(`/subscribers/me/onboardingpreferences`, {
    method: "POST",
    body: payload,
  });
}
